import { setExerciseTimeout } from "../exercises/pause-clock";
import { useLearningDisplayItem } from "../exercises/LearningDisplayBoundary";
import { useRecommendationRunStarter } from "../exercises/recommendation-context";
import { useEffect, useState } from "react";
import type { SortierspielExerciseConfig, SortierspielRunSettings } from "@calega/content";
import type { SortierspielRun } from "@calega/exercise-engine";
import type { Account } from "../lib/types";
import { useRealtime } from "../realtime/RealtimeContext";
import { ExerciseMenu } from "./ExerciseMenu";
import { ExerciseWaitingCard } from "./ExerciseWaitingCard";
import { abortExerciseRun, submitExerciseAnswer } from "../exercises/learning-api";
import { CompletedRunActions } from "./CompletedRunActions";
import { CompletedRunHistory } from "./CompletedRunHistory";
import { RunCompletion, useRunCompletion } from "./RunCompletion";

type Phase = "running" | "complete";
interface SortierspielState {
  runId: string;
  run: SortierspielRun;
  phase: Phase;
  selectedObjectId: string | null;
  placedObjectIds: string[];
  wrongAttempts: number;
  wrongObjectId: string | null;
  result: { score: number; maxScore: number } | null;
}

export function Sortierspiel({ account, config, pageId }: { account: Account; config: SortierspielExerciseConfig; pageId: string }) {
  const startExerciseRun = useRecommendationRunStarter();
  const completion = useRunCompletion();
  const realtime = useRealtime();
  const facilitator = account.roles.some((role) => role === "ADMIN" || role === "ORGANIZATION" || role === "THERAPIST");
  const follower = Boolean(realtime.session && !facilitator);
  const canControl = !realtime.session || facilitator;
  const canAnswer = !realtime.session || facilitator || follower;
  const [categoryCount, setCategoryCount] = useState(config.defaultCategoryCount);
  const [objectsPerCategory, setObjectsPerCategory] = useState(config.defaultObjectsPerCategory);
  const [exercise, setExercise] = useState<SortierspielState | null>(null);
  useLearningDisplayItem(exercise?.runId, 0, Boolean(exercise && exercise.phase !== "complete"));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const publish = (state: SortierspielState | null) => realtime.publishExercise({ exerciseType: "sortierspiel", pageId, state });
  useEffect(() => {
    const synced = realtime.state.exercise as { exerciseType?: string; pageId?: string; state?: SortierspielState | null } | null;
    if (synced?.exerciseType === "sortierspiel" && synced.pageId === pageId) setExercise(synced.state ?? null);
  }, [realtime.state.exercise, pageId]);

  const settings = (): SortierspielRunSettings => ({ categoryCount, objectsPerCategory });
  async function start(runSettings = settings()) {
    if (busy || !canControl) return;
    setBusy(true);
    try {
      const response = await startExerciseRun<{ runId: string; exercise: SortierspielRun }>({ contentPageId: pageId, settings: runSettings });
      const next: SortierspielState = { runId: response.runId, run: response.exercise, phase: "running", selectedObjectId: null, placedObjectIds: [], wrongAttempts: 0, wrongObjectId: null, result: null };
      setExercise(next);
      setMessage("");
      publish(next);
    } finally {
      setBusy(false);
    }
  }

  async function abort() {
    if (exercise && exercise.phase !== "complete") await abortExerciseRun(exercise.runId);
  }

  async function finish(current: SortierspielState) {
    if (current.phase === "complete" || !canControl) return;
    await completion.complete(current.runId, (result) => {
      const next = { ...current, result, phase: "complete" as const, selectedObjectId: null, wrongObjectId: null };
      setExercise(next);
      publish(next);
    });
  }

  useEffect(() => {
    if (!exercise || !canControl || busy || exercise.phase !== "running" || exercise.placedObjectIds.length !== exercise.run.objects.length) return;
    finish(exercise).catch((error) => setMessage(error.message));
  }, [exercise?.phase, exercise?.placedObjectIds.length, canControl, busy]);

  async function backToSettings() {
    if (!exercise || busy || !canControl) return;
    setBusy(true);
    try {
      await abort();
      setCategoryCount(exercise.run.settings.categoryCount);
      setObjectsPerCategory(exercise.run.settings.objectsPerCategory);
      setExercise(null);
      setMessage("");
      publish(null);
    } finally {
      setBusy(false);
    }
  }

  async function remix() {
    if (!exercise || busy || !canControl) return;
    const runSettings = exercise.run.settings;
    setBusy(true);
    try {
      await abort();
    } finally {
      setBusy(false);
    }
    await start(runSettings);
  }

  async function closeCompletedRun(repeat: boolean) {
    if (!exercise || exercise.phase !== "complete") return;
    if (repeat) await remix();
    else await backToSettings();
  }

  function selectObject(objectId: string) {
    if (!exercise || exercise.phase !== "running" || busy || !canAnswer || exercise.placedObjectIds.includes(objectId)) return;
    const next = { ...exercise, selectedObjectId: exercise.selectedObjectId === objectId ? null : objectId, wrongObjectId: null };
    setExercise(next);
    publish(next);
  }

  async function assign(categoryId: string, suppliedObjectId?: string) {
    if (!exercise || exercise.phase !== "running" || busy || !canAnswer) return;
    const objectId = suppliedObjectId ?? exercise.selectedObjectId;
    const object = exercise.run.objects.find(({ id }) => id === objectId);
    if (!object || exercise.placedObjectIds.includes(object.id)) return;
    setBusy(true);
    try {
      await submitExerciseAnswer(exercise.runId, { type: "sortierspiel", objectId: object.id, categoryId });
      if (object.categoryId !== categoryId) {
        const next = { ...exercise, selectedObjectId: null, wrongObjectId: object.id, wrongAttempts: exercise.wrongAttempts + 1 };
        setExercise(next);
        publish(next);
        setExerciseTimeout(() => setExercise((current) => current?.runId === next.runId && current.wrongObjectId === object.id ? { ...current, wrongObjectId: null } : current), 600);
        return;
      }
      const placedObjectIds = [...exercise.placedObjectIds, object.id];
      const next = { ...exercise, selectedObjectId: null, wrongObjectId: null, placedObjectIds };
      setExercise(next);
      publish(next);
      if (placedObjectIds.length === exercise.run.objects.length && canControl) await finish(next);
    } finally {
      setBusy(false);
    }
  }

  if (completion.status.phase !== "idle") return <RunCompletion completion={completion} />;

  if (follower && !exercise) return <ExerciseWaitingCard className="sortierspiel-exercise" />;
  if (!exercise) return <section className="sortierspiel-exercise">
    <p className="quiz-instructions learning-text">{config.instructions}</p>
    {config.mode === "random" && <div className="mathe-settings sortierspiel-settings">
      <fieldset><legend>Anzahl Gruppen</legend><div className="mathe-option-grid">{[2, 3, 4].map((value) => <label className="checkbox-label" key={value}><input type="radio" name={`${pageId}-sort-categories`} checked={categoryCount === value} onChange={() => setCategoryCount(value)} />{value}</label>)}</div></fieldset>
      <fieldset><legend>Objekte pro Gruppe</legend><div className="mathe-option-grid">{[3, 4, 5, 6].map((value) => <label className="checkbox-label" key={value}><input type="radio" name={`${pageId}-sort-objects`} checked={objectsPerCategory === value} onChange={() => setObjectsPerCategory(value)} />{value}</label>)}</div></fieldset>
    </div>}
    <button className="primary" disabled={busy || !canControl} onClick={() => start().catch((error) => setMessage(error.message))}>Sortieraufgabe starten</button>
    {message && <div className="alert info">{message}</div>}
  </section>;

  const available = exercise.run.objects.filter(({ id }) => !exercise.placedObjectIds.includes(id));
  const selectedObject = exercise.run.objects.find(({ id }) => id === exercise.selectedObjectId) ?? null;
  return <section className="sortierspiel-exercise">
    {canControl && exercise.phase !== "complete" && <ExerciseMenu><button className="secondary small" disabled={busy} onClick={() => backToSettings().catch((error) => setMessage(error.message))}>← Zurück zu den Einstellungen</button><button className="secondary small" disabled={busy} onClick={() => remix().catch((error) => setMessage(error.message))}>↻ Neu mischen</button></ExerciseMenu>}
    {exercise.phase === "complete" ? <><div className="sortierspiel-result"><strong>Alle {exercise.result?.maxScore ?? exercise.run.objects.length} Objekte richtig sortiert.</strong><p>{exercise.wrongAttempts ? `${exercise.wrongAttempts} Zuordnung${exercise.wrongAttempts === 1 ? "" : "en"} wurde${exercise.wrongAttempts === 1 ? "" : "n"} noch einmal probiert.` : "Das war fehlerfrei."}</p><p>Der Durchlauf ist abgeschlossen.</p></div>{canControl && <CompletedRunActions onFinish={() => closeCompletedRun(false)} onRepeat={() => closeCompletedRun(true)} onError={setMessage} />}{exercise.phase === "complete" && <CompletedRunHistory key={exercise.runId} runId={exercise.runId} />}</> : <>
      <div className="sortierspiel-card">
        <p className="sortierspiel-prompt learning-text">Wähle ein Objekt und dann die passende Gruppe – oder ziehe es direkt in die Gruppe.</p>
        <div className="sortierspiel-progress">{exercise.placedObjectIds.length} von {exercise.run.objects.length} Objekten sortiert</div>
        <div className="sortierspiel-pool" aria-label="Noch zu sortierende Objekte">{available.map((object) => <button key={object.id} draggable={canAnswer && !busy} onDragStart={(event) => { event.dataTransfer.setData("text/plain", object.id); selectObject(object.id); }} className={`sortierspiel-object ${selectedObject?.id === object.id ? "selected" : ""} ${exercise.wrongObjectId === object.id ? "wrong" : ""}`} disabled={busy || !canAnswer} onClick={() => selectObject(object.id)} aria-pressed={selectedObject?.id === object.id}>{object.emoji}</button>)}</div>
        <div className="sortierspiel-categories">{exercise.run.categories.map((category) => {
          const placed = exercise.run.objects.filter((object) => object.categoryId === category.id && exercise.placedObjectIds.includes(object.id));
          return <button key={category.id} className={`sortierspiel-category learning-text ${selectedObject ? "is-target" : ""}`} disabled={busy || !canAnswer} onDragOver={(event) => event.preventDefault()} onDrop={(event) => { event.preventDefault(); assign(category.id, event.dataTransfer.getData("text/plain")).catch((error) => setMessage(error.message)); }} onClick={() => assign(category.id).catch((error) => setMessage(error.message))}><strong>{category.label}</strong><span>{placed.map((object) => <i key={object.id}>{object.emoji}</i>)}</span></button>;
        })}</div>
      </div>
      {selectedObject && <p className="sortierspiel-selection">Ausgewählt: {selectedObject.emoji} – wähle jetzt die passende Gruppe.</p>}
      {!canControl && <p className="muted">Die Übung wird von der Sitzungsleitung beendet.</p>}
    </>}
    {message && <div className="alert info">{message}</div>}
  </section>;
}
